import * as THREE from 'three';
import { WallManager } from './WallManager.js';
import { BeamManager, BEAM_TYPES } from './BeamManager.js';
import { LightingManager } from './LightingManager.js';
import { ZoneManager, ROOF_TYPES } from './ZoneManager.js';
import { ShadeManager } from './ShadeManager.js';

// ─── Azenco R-Blade Specifications (inches → meters) ───────────────────────
const IN = 0.0254; // 1 inch in meters

const SPEC = {
  // Overall max size
  MAX_WIDTH: 192 * IN,   // 16'-0"
  MAX_LENGTH: 276 * IN,  // 23'-0"

  // Post: 6 1/2" × 6 1/2"
  POST_SIZE: 6.5 * IN,
  POST_WALL: (1 + 5 / 32) * IN,

  // Flat beam: 14" tall, 5 3/4" flange — single piece, no separate top beam
  MAIN_BEAM_HEIGHT: 14 * IN,
  MAIN_BEAM_FLANGE: 5.75 * IN,
  SEAM_GAP: (1 / 6) * IN,  // 1/6" centered seam between the two joined halves

  // Combined fascia: 14"
  FASCIA_HEIGHT: 14 * IN,

  // Blade / louver profile
  BLADE_CHORD: 9.5 * IN,     // visible width of blade cross-section
  BLADE_HEIGHT: 4.625 * IN,  // blade height dimension
  BLADE_DEPTH: 3.625 * IN,   // blade depth dimension
  BLADE_THICK: 2.125 * IN,   // material thickness

  // Beam connection plate: 11 3/8" × 11 3/8"
  CONN_PLATE: 11.375 * IN,

  // Edge header: 3 1/8" × 3/4"
  EDGE_HEADER_H: 3.125 * IN,
  EDGE_HEADER_D: 0.75 * IN,

  // Corner angle: 3 1/8" × 3 1/8", 1/2" thick
  CORNER_SIZE: 3.125 * IN,
  CORNER_THICK: 0.5 * IN,

  // Post cover: 3 3/8" × 1"
  POST_COVER_W: 3.375 * IN,
  POST_COVER_D: 1.0 * IN,

  // Fan beam: 4" × 4"
  FAN_BEAM: 4.0 * IN,
};

// Default color scheme — RAL 9003 Signal White (calibrated for metalness=0.85 rendering)
const COLORS = {
  post: 0xf4f0ec,
  mainBeam: 0xf4f0ec,
  topBeam: 0xf4f0ec,
  blade: 0xf4f0ec,
  connector: 0xf4f0ec,
  edgeTrim: 0xf4f0ec,
};

export class PergolaBuilder {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'pergola';
    this.scene.add(this.group);

    this.blades = [];
    this.bladeAngle = 45; // degrees: 0 = closed (flat), 110 = fully open
    this.wallManager = new WallManager();
    this.beamManager = new BeamManager();
    this.lightingManager = new LightingManager();
    this.zoneManager = new ZoneManager();
    this.shadeManager = new ShadeManager();
    this.lightObjects = []; // track point lights for cleanup

    // Configurable dimensions (in meters internally, UI shows ft-in)
    this.config = {
      width: 16 * 12 * IN,   // default 16' outside frame width
      length: 23 * 12 * IN,  // default 23' outside frame length
      postHeight: 10 * 12 * IN, // default 10' post height (to underside of beam)
      postColor: COLORS.post,
      beamColor: COLORS.mainBeam,
      bladeColor: COLORS.blade,
      walls: { front: false, back: false, left: false, right: false },
      // Architecture options
      corbelEnds: false,
      corniceStyle: 'none', // 'none', '1step', '2step'
      corniceLed: false,
      // Floor type: 'wood', 'stone', or 'grass'
      floorType: 'wood',
    };

    // Roof animation state
    this.roofAnimating = false;
    this.roofTargetAngle = null;

    this.materials = {};
    this._textureCache = {}; // Cache procedural textures across rebuilds
    this.buildMaterials();
    this.build();
  }

  buildMaterials() {
    // Powder-coated aluminum: satin metal finish (high metalness, moderate roughness)
    // Real powder coat sits on top of aluminum — still acts metallic under PBR
    this.materials.post = new THREE.MeshStandardMaterial({
      color: this.config.postColor,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.mainBeam = new THREE.MeshStandardMaterial({
      color: this.config.beamColor,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.topBeam = new THREE.MeshStandardMaterial({
      color: COLORS.topBeam,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.blade = new THREE.MeshStandardMaterial({
      color: this.config.bladeColor,
      roughness: 0.32,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.connector = new THREE.MeshStandardMaterial({
      color: COLORS.connector,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.edgeTrim = new THREE.MeshStandardMaterial({
      color: COLORS.edgeTrim,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.wall = new THREE.MeshStandardMaterial({
      color: 0xc2b8a3,   // light stucco / building facade
      roughness: 0.92,
      metalness: 0.02,
    });
    // Beam materials by type — match anodized aluminum PBR
    // Fan beam defaults to frame color (mainBeam), not its own blue-gray
    this.materials.beamAccessory = this.materials.mainBeam;
    this.materials.beamStructural = new THREE.MeshStandardMaterial({
      color: BEAM_TYPES.structural.color, roughness: 0.35, metalness: 0.92,
    });
    this.materials.beamHeater = new THREE.MeshStandardMaterial({
      color: BEAM_TYPES.heater.color, roughness: 0.35, metalness: 0.92,
    });
    this.materials.beamReinforcement = new THREE.MeshStandardMaterial({
      color: BEAM_TYPES.reinforcement.color, roughness: 0.35, metalness: 0.92,
    });
    // Lighting materials
    this.materials.recessedLight = new THREE.MeshStandardMaterial({
      color: 0xfff8e1, emissive: 0xfff0b0, emissiveIntensity: 0.6,
      roughness: 0.3, metalness: 0.1,
    });
    this.materials.ledBar = new THREE.MeshStandardMaterial({
      color: 0xe0e0e0, emissive: 0xffffff, emissiveIntensity: 0.4,
      roughness: 0.2, metalness: 0.6,
    });
    this.materials.ledStrip = new THREE.MeshStandardMaterial({
      color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.8,
      roughness: 0.1, metalness: 0.2,
    });
    this.materials.solarLight = new THREE.MeshStandardMaterial({
      color: 0x333333, roughness: 0.4, metalness: 0.6,
    });
    this.materials.fanHub = new THREE.MeshStandardMaterial({
      color: 0x444444, roughness: 0.3, metalness: 0.8,
    });
    this.materials.fanBlade = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a, roughness: 0.6, metalness: 0.4,
    });
    // Architecture materials — match aluminum PBR
    this.materials.corbel = new THREE.MeshStandardMaterial({
      color: COLORS.mainBeam, roughness: 0.34, metalness: 0.92,
    });
    this.materials.cornice = new THREE.MeshStandardMaterial({
      color: COLORS.topBeam, roughness: 0.32, metalness: 0.93,
    });
    this.materials.corniceLed = new THREE.MeshStandardMaterial({
      color: 0xffffff, emissive: 0xfff5cc, emissiveIntensity: 0.5,
      roughness: 0.1, metalness: 0.2,
    });
    // R-Shade fabric material
    this.materials.shadeFabric = new THREE.MeshStandardMaterial({
      color: 0xd4c5a9,        // warm beige fabric
      roughness: 0.95,
      metalness: 0.0,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.92,
    });
    // R-Breeze screen material
    this.materials.breezeScreen = new THREE.MeshStandardMaterial({
      color: 0x3a3a3a,        // dark charcoal screen
      roughness: 0.8,
      metalness: 0.1,
      transparent: true,
      opacity: 0.7,
    });
    // Magnatrack solar mesh — near-black, semi-transparent, double-sided
    this.materials.shadeMesh = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      roughness: 0.85,
      metalness: 0.05,
      transparent: true,
      opacity: 0.65,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    // Shade housing / tracks — aluminum powder coat matching post color
    this.materials.shadeHousing = new THREE.MeshStandardMaterial({
      color: this.config.postColor,
      roughness: 0.28,
      metalness: 0.85,
      envMapIntensity: 1.0,
    });
    this.materials.junctionBeam = new THREE.MeshStandardMaterial({
      color: COLORS.mainBeam,
      roughness: 0.32,
      metalness: 0.93,
    });
    // Floor materials — use cached textures to avoid regenerating 3000+ random calls per build
    this.materials.woodDecking = this._getCachedFloorMaterial('wood');
    this.materials.stonePatio = this._getCachedFloorMaterial('stone');
    this.materials.grass = this._getCachedFloorMaterial('grass');
  }

  _getCachedFloorMaterial(type) {
    if (this._textureCache[type]) return this._textureCache[type];
    let mat;
    switch (type) {
      case 'wood': mat = this._createWoodDeckingMaterial(); break;
      case 'stone': mat = this._createStonePatioMaterial(); break;
      case 'grass': mat = this._createGrassMaterial(); break;
    }
    this._textureCache[type] = mat;
    return mat;
  }

  _createWoodDeckingMaterial() {
    // Procedural wood grain using canvas texture
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Base wood color
    ctx.fillStyle = '#b5874b';
    ctx.fillRect(0, 0, 512, 512);

    // Wood planks with grain lines
    const plankWidth = 64;
    for (let x = 0; x < 512; x += plankWidth) {
      // Plank gap
      ctx.fillStyle = '#8a6530';
      ctx.fillRect(x, 0, 2, 512);

      // Wood grain lines
      for (let g = 0; g < 20; g++) {
        const gx = x + 4 + Math.random() * (plankWidth - 8);
        ctx.strokeStyle = `rgba(${140 + Math.random() * 30}, ${90 + Math.random() * 30}, ${40 + Math.random() * 20}, ${0.15 + Math.random() * 0.2})`;
        ctx.lineWidth = 0.5 + Math.random() * 1.5;
        ctx.beginPath();
        let y = 0;
        ctx.moveTo(gx, y);
        while (y < 512) {
          y += 3 + Math.random() * 6;
          ctx.lineTo(gx + (Math.random() - 0.5) * 3, y);
        }
        ctx.stroke();
      }

      // Subtle color variation per plank
      ctx.fillStyle = `rgba(${Math.random() > 0.5 ? 180 : 100}, ${Math.random() > 0.5 ? 120 : 70}, 40, 0.06)`;
      ctx.fillRect(x + 2, 0, plankWidth - 2, 512);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(8, 8);

    return new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.75,
      metalness: 0.0,
    });
  }

  _createStonePatioMaterial() {
    // Procedural stone patio texture
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Base stone color
    ctx.fillStyle = '#c4b8a8';
    ctx.fillRect(0, 0, 512, 512);

    // Stone tiles with grout lines
    const tileSize = 128;
    for (let ty = 0; ty < 512; ty += tileSize) {
      for (let tx = 0; tx < 512; tx += tileSize) {
        // Grout lines
        ctx.fillStyle = '#8a8070';
        ctx.fillRect(tx, ty, tileSize, 3);
        ctx.fillRect(tx, ty, 3, tileSize);

        // Stone face with subtle variation
        const r = 170 + Math.random() * 30;
        const g = 160 + Math.random() * 25;
        const b = 145 + Math.random() * 20;
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fillRect(tx + 3, ty + 3, tileSize - 6, tileSize - 6);

        // Stone texture noise
        for (let n = 0; n < 60; n++) {
          const nx = tx + 5 + Math.random() * (tileSize - 10);
          const ny = ty + 5 + Math.random() * (tileSize - 10);
          ctx.fillStyle = `rgba(${120 + Math.random() * 60}, ${110 + Math.random() * 50}, ${100 + Math.random() * 40}, 0.12)`;
          ctx.fillRect(nx, ny, 2 + Math.random() * 4, 2 + Math.random() * 4);
        }
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(6, 6);

    return new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.85,
      metalness: 0.02,
    });
  }

  _createGrassMaterial() {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Base green grass
    ctx.fillStyle = '#4a8c3f';
    ctx.fillRect(0, 0, 512, 512);

    // Grass blade texture — many short strokes
    for (let i = 0; i < 3000; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const len = 3 + Math.random() * 8;
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.6;
      const r = 50 + Math.random() * 50;
      const g = 110 + Math.random() * 60;
      const b = 30 + Math.random() * 30;
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.3 + Math.random() * 0.4})`;
      ctx.lineWidth = 0.5 + Math.random() * 1.2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
      ctx.stroke();
    }

    // Subtle light/dark patches
    for (let i = 0; i < 20; i++) {
      const px = Math.random() * 512;
      const py = Math.random() * 512;
      const pr = 20 + Math.random() * 40;
      const bright = Math.random() > 0.5;
      ctx.fillStyle = bright
        ? `rgba(100, 180, 70, 0.08)`
        : `rgba(30, 60, 20, 0.08)`;
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(10, 10);

    return new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.95,
      metalness: 0.0,
    });
  }

  build() {
    this.clear();
    const c = this.config;
    const zones = this.zoneManager.getZones();
    const offsets = this.zoneManager.getZoneOffsets();
    const isMultiZone = zones.length > 1;
    const isWidthAxis = this.zoneManager.isWidthAxis();

    // Sync zone manager's shared dimensions with config
    this.zoneManager.setSharedWidth(c.width / IN);
    this.zoneManager.setSharedLength(c.length / IN);

    // Overall footprint
    const totalLengthIn = isMultiZone ? this.zoneManager.getTotalLength() : c.length / IN;
    const totalWidthIn = isMultiZone ? this.zoneManager.getTotalWidth() : c.width / IN;

    // Auto-detect structural header beam for span validation
    const hasHeader = this.beamManager.getBeams().some(b => b.type === 'structural');
    this.wallManager.setHasStructuralHeader(hasHeader);

    // Recalculate wall/post state
    this.wallManager.setWalls(c.walls);
    if (isMultiZone) {
      this.wallManager.recalculateMultiZone(totalWidthIn, totalLengthIn, offsets, isWidthAxis);
    } else {
      this.wallManager.recalculate(totalWidthIn, totalLengthIn);
    }

    // Overall build config uses total footprint
    const buildConfig = isMultiZone
      ? { ...c, width: totalWidthIn * IN, length: totalLengthIn * IN }
      : c;

    this.buildWalls(buildConfig);
    this.buildPosts(buildConfig);

    if (isWidthAxis && isMultiZone) {
      // ── Width-axis zones: stack along X ──
      // Front/back fascia: continuous along full total width
      this.buildFrontBackFascia(buildConfig);

      // Side fascia: only at outer X edges
      const beamW = SPEC.MAIN_BEAM_FLANGE;
      const halfW = buildConfig.width / 2;
      this._buildBeamAssembly(buildConfig.length, -halfW + beamW / 2, 0, true, -1);
      this._buildBeamAssembly(buildConfig.length, halfW - beamW / 2, 0, true, +1);

      // Per-zone: side beams at zone boundaries and blades
      for (let i = 0; i < zones.length; i++) {
        const zone = zones[i];
        const offset = offsets[i];
        const zoneConfig = {
          ...c,
          width: offset.widthInches * IN,
          length: totalLengthIn * IN,
        };
        const xCenterM = offset.centerX * IN;
        this.buildZoneRoof(zone, zoneConfig, 0, true, true, xCenterM);
      }

      // Junction beams between width zones (run along Z)
      this.buildWidthJunctionBeams(zones, offsets);
    } else {
      // ── Length-axis zones (default): stack along Z ──
      this.buildSideFascia(buildConfig);

      for (let i = 0; i < zones.length; i++) {
        const zone = zones[i];
        const offset = offsets[i];
        const zoneConfig = {
          ...c,
          length: zone.lengthInches * IN,
        };
        const zCenterM = offset.centerZ * IN;

        const isFirst = i === 0;
        const isLast = i === zones.length - 1;
        this.buildZoneFrontBackBeams(zoneConfig, zCenterM, isFirst, isLast);
        this.buildZoneRoof(zone, zoneConfig, zCenterM, isFirst, isLast);
      }

      if (isMultiZone) {
        this.buildJunctionBeams(zones, offsets);
      }
    }

    this.buildCustomBeams(buildConfig);
    this.buildLighting(buildConfig);
    this.buildArchitecture(buildConfig);

    buildConfig.shades = this.shadeManager.serialize();
    this.buildShades(buildConfig);
  }

  clear() {
    // Remove point lights from scene (they're added to scene, not group)
    for (const light of this.lightObjects) {
      this.scene.remove(light);
      if (light.dispose) light.dispose();
    }
    this.lightObjects = [];

    // Track materials to dispose (avoid double-dispose of shared materials)
    const disposedMats = new Set();

    while (this.group.children.length > 0) {
      const child = this.group.children[0];
      this.group.remove(child);
      if (child.geometry) child.geometry.dispose();
      // Dispose per-mesh materials that aren't in the shared cache
      if (child.material && !Object.values(this.materials).includes(child.material)) {
        if (!disposedMats.has(child.material)) {
          child.material.dispose();
          disposedMats.add(child.material);
        }
      }
    }
    this.blades = [];
  }

  addMesh(geo, mat, pos, part) {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(pos.x, pos.y, pos.z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.part = part;
    this.group.add(mesh);
    return mesh;
  }

  // ─── Walls (building facade panels) ────────────────────────────
  buildWalls(c) {
    const wallThick = 6 * IN;          // 6" thick facade
    const overhang = 24 * IN;          // wall extends 2' past pergola edges
    const wallHeight = c.postHeight + SPEC.EDGE_HEADER_H;
    const halfW = c.width / 2;
    const halfL = c.length / 2;
    const wallY = wallHeight / 2;

    if (c.walls.front) {
      const geo = new THREE.BoxGeometry(c.width + overhang * 2, wallHeight, wallThick);
      this.addMesh(geo, this.materials.wall,
        { x: 0, y: wallY, z: -halfL - wallThick / 2 }, 'wall');
    }
    if (c.walls.back) {
      const geo = new THREE.BoxGeometry(c.width + overhang * 2, wallHeight, wallThick);
      this.addMesh(geo, this.materials.wall,
        { x: 0, y: wallY, z: halfL + wallThick / 2 }, 'wall');
    }
    if (c.walls.left) {
      const geo = new THREE.BoxGeometry(wallThick, wallHeight, c.length + overhang * 2);
      this.addMesh(geo, this.materials.wall,
        { x: -halfW - wallThick / 2, y: wallY, z: 0 }, 'wall');
    }
    if (c.walls.right) {
      const geo = new THREE.BoxGeometry(wallThick, wallHeight, c.length + overhang * 2);
      this.addMesh(geo, this.materials.wall,
        { x: halfW + wallThick / 2, y: wallY, z: 0 }, 'wall');
    }
  }

  // ─── Posts (notched: solid stops below beam, outer walls wrap around beam) ──
  buildPosts(c) {
    const ps     = SPEC.POST_SIZE;           // 6.5" square
    const pw     = SPEC.POST_WALL;           // 1 5/32" wall thickness
    const beamH  = SPEC.MAIN_BEAM_HEIGHT;    // 14"
    const beamW  = SPEC.MAIN_BEAM_FLANGE;    // 5.75" beam depth
    const reveal = 0.125 * IN;               // 1/8" post walls above beam top

    // Solid post body: floor to beam underside (beam rests on top of this)
    const solidH  = c.postHeight - beamH;
    // Notch walls: only the OUTER faces of the post, wrapping around the beam
    const notchH  = beamH + reveal;
    const notchCY = solidH + notchH / 2;

    const solidGeo = new THREE.BoxGeometry(ps, solidH, ps);

    for (const pos of this.wallManager.postPositions) {
      const xSign = pos.x < 0 ? 1 : pos.x > 0 ? -1 : 0;
      const zSign = pos.z < 0 ? 1 : pos.z > 0 ? -1 : 0;

      let mx, mz;
      if (pos.type === 'corner') {
        mx = pos.x * IN + xSign * (ps / 2);
        mz = pos.z * IN + zSign * (ps / 2);
      } else if (pos.type === 'junction') {
        mx = pos.x * IN + xSign * (ps / 2);
        mz = pos.z * IN;
      } else {
        if (pos.edge === 'front' || pos.edge === 'back') {
          mx = pos.x * IN;
          mz = pos.z * IN + zSign * (ps / 2);
        } else {
          mx = pos.x * IN + xSign * (ps / 2);
          mz = pos.z * IN;
        }
      }

      // Solid body below beam — beam rests on top of this
      this.addMesh(solidGeo, this.materials.post, { x: mx, y: solidH / 2, z: mz }, 'post');

      // Notch walls: only the OUTER faces wrap up around the beam.
      // At corners, both outer faces (the two faces pointing away from pergola center).
      // The inner faces are open — the beam sits in the notch there.
      // Determine which faces are "outer" based on post position relative to center
      const isLeftSide  = pos.x < 0;
      const isRightSide = pos.x > 0;
      const isFrontSide = pos.z < 0;
      const isBackSide  = pos.z > 0;

      // X-facing walls (left/right outer faces)
      if (pos.type === 'corner' || pos.type === 'junction') {
        // Outer X face: the face pointing AWAY from center
        const outerX = isLeftSide ? mx - ps / 2 + pw / 2 : mx + ps / 2 - pw / 2;
        const xWallGeo = new THREE.BoxGeometry(pw, notchH, ps);
        this.addMesh(xWallGeo, this.materials.post, { x: outerX, y: notchCY, z: mz }, 'post');
      }

      // Z-facing walls (front/back outer faces)
      if (pos.type === 'corner') {
        // Outer Z face: the face pointing AWAY from center
        const outerZ = isFrontSide ? mz - ps / 2 + pw / 2 : mz + ps / 2 - pw / 2;
        const zWallGeo = new THREE.BoxGeometry(ps, notchH, pw);
        this.addMesh(zWallGeo, this.materials.post, { x: mx, y: notchCY, z: outerZ }, 'post');
      }

      // For edge posts (non-corner), add the outer face along the edge
      if (pos.type === 'intermediate') {
        if (pos.edge === 'front' || pos.edge === 'back') {
          const outerZ = isFrontSide ? mz - ps / 2 + pw / 2 : mz + ps / 2 - pw / 2;
          const zWallGeo = new THREE.BoxGeometry(ps, notchH, pw);
          this.addMesh(zWallGeo, this.materials.post, { x: mx, y: notchCY, z: outerZ }, 'post');
        } else {
          const outerX = isLeftSide ? mx - ps / 2 + pw / 2 : mx + ps / 2 - pw / 2;
          const xWallGeo = new THREE.BoxGeometry(pw, notchH, ps);
          this.addMesh(xWallGeo, this.materials.post, { x: outerX, y: notchCY, z: mz }, 'post');
        }
      }
    }
  }

  // ─── Fascia beam assembly helper ──────────────────────────────
  _buildBeamAssembly(length, centerX, centerZ, isLengthBeam, outerDir) {
    const beamH = SPEC.MAIN_BEAM_HEIGHT;
    const seam  = SPEC.SEAM_GAP;
    const topOfPost = this.config.postHeight;
    const gutterH = 4.125 * IN;           // 4 1/8" gutter depth per Azenco spec
    const gutterW = 5.5 * IN;
    const flangeThick = 0.5 * IN;
    const bottomThick = 0.375 * IN;
    const innerLipH = gutterH;             // inner wall = full gutter depth (blade-side)
    const outerLipH = gutterH;
    const fasciaH = beamH - gutterH - seam;
    const fasciaThick = flangeThick;
    const gutterTopY = topOfPost - fasciaH - seam;
    const gutterBotY = gutterTopY - gutterH;
    const fasciaBotY = gutterTopY + seam;
    const seamMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.9, metalness: 0.1 });

    if (isLengthBeam) {
      const outerX = centerX + outerDir * (gutterW / 2 - flangeThick / 2);
      const innerX = centerX - outerDir * (gutterW / 2 - flangeThick / 2);
      this.addMesh(new THREE.BoxGeometry(flangeThick, outerLipH, length), this.materials.mainBeam,
        { x: outerX, y: gutterBotY + outerLipH / 2, z: centerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(flangeThick, innerLipH, length), this.materials.mainBeam,
        { x: innerX, y: gutterBotY + innerLipH / 2, z: centerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(gutterW, bottomThick, length), this.materials.mainBeam,
        { x: centerX, y: gutterBotY + bottomThick / 2, z: centerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(fasciaThick, fasciaH, length), this.materials.mainBeam,
        { x: outerX, y: fasciaBotY + fasciaH / 2, z: centerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(fasciaThick + 0.001, seam, length), seamMat,
        { x: outerX, y: gutterTopY + seam / 2, z: centerZ }, 'beam-sep');
    } else {
      const outerZ = centerZ + outerDir * (gutterW / 2 - flangeThick / 2);
      const innerZ = centerZ - outerDir * (gutterW / 2 - flangeThick / 2);
      this.addMesh(new THREE.BoxGeometry(length, outerLipH, flangeThick), this.materials.mainBeam,
        { x: centerX, y: gutterBotY + outerLipH / 2, z: outerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(length, innerLipH, flangeThick), this.materials.mainBeam,
        { x: centerX, y: gutterBotY + innerLipH / 2, z: innerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(length, bottomThick, gutterW), this.materials.mainBeam,
        { x: centerX, y: gutterBotY + bottomThick / 2, z: centerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(length, fasciaH, fasciaThick), this.materials.mainBeam,
        { x: centerX, y: fasciaBotY + fasciaH / 2, z: outerZ }, 'mainBeam');
      this.addMesh(new THREE.BoxGeometry(length, seam, fasciaThick + 0.001), seamMat,
        { x: centerX, y: gutterTopY + seam / 2, z: outerZ }, 'beam-sep');
    }
  }

  // ─── Side fascia: continuous left + right beams (total length, no gaps) ──
  buildSideFascia(c) {
    const beamW = SPEC.MAIN_BEAM_FLANGE;
    const halfW = c.width / 2;
    // Side beams run the full total length, centered at Z=0
    this._buildBeamAssembly(c.length, -halfW + beamW / 2, 0, true, -1);
    this._buildBeamAssembly(c.length, halfW - beamW / 2, 0, true, +1);
  }

  // ─── Front/back fascia: continuous front + back beams (for width-axis zones) ──
  buildFrontBackFascia(c) {
    const beamW = SPEC.MAIN_BEAM_FLANGE;
    const halfL = c.length / 2;
    // Full width for corner overlap (same fix as buildZoneFrontBackBeams)
    this._buildBeamAssembly(c.width, 0, -halfL + beamW / 2, false, -1);
    this._buildBeamAssembly(c.width, 0, halfL - beamW / 2, false, +1);
  }

  // ─── Per-zone front/back beams (only at outer edges) ────────
  buildZoneFrontBackBeams(c, zCenter, isFirst, isLast) {
    const beamW = SPEC.MAIN_BEAM_FLANGE;
    const halfL = c.length / 2;
    // Use full width so front/back fascia overlaps into corners with side fascia.
    // Without this, sliding a post (cantilever) exposes the corner gap where
    // only the side fascia reaches — the corner joint needs both pieces.
    if (isFirst) {
      this._buildBeamAssembly(c.width, 0, zCenter - halfL + beamW / 2, false, -1);
    }
    if (isLast) {
      this._buildBeamAssembly(c.width, 0, zCenter + halfL - beamW / 2, false, +1);
    }
  }

  // ─── Zone Roof (dispatches to roof type builder) ───────────────
  buildZoneRoof(zone, c, zCenter, isFirst = true, isLast = true, xCenter = 0) {
    const roofType = zone.roofType || 'r-blade';
    switch (roofType) {
      case 'r-blade':
        this.buildBladesForZone(c, zCenter, xCenter);
        break;
      case 'r-shade':
        this.buildShadeForZone(c, zCenter);
        break;
      case 'r-breeze':
        this.buildBreezeForZone(c, zCenter);
        break;
    }
  }

  // ─── R-Blade: Louver blades for a single zone ─────────────────
  // Blades open toward the motor side (back/+Z). Non-motor end (front/-Z) has
  // a fixed gap-closure louver that stays closed to fill any remaining gap.
  buildBladesForZone(c, zCenter, xCenter = 0) {
    const mbW = SPEC.MAIN_BEAM_FLANGE;
    const halfL = c.length / 2;

    // Blades span the WIDTH (X axis), overlapping into the gutter by 1" per Azenco spec
    const gutterOverlap = 1.0 * IN;
    const bladeSpan = c.width - mbW * 2 + gutterOverlap * 2;

    // Blade cross-section: 9.5" chord rounded rectangle
    const chord = SPEC.BLADE_CHORD;
    const bladeThick = SPEC.BLADE_THICK * 0.4;

    const shape = new THREE.Shape();
    const hh = bladeThick / 2;
    const r = Math.min(chord, hh) * 0.3;

    // Pivot at edge (X=0, Y=0 = gutter lip contact). Chord extends from 0 to +chord.
    // After rotateY(PI/2), chord lies along +Z. Rotation around X swings entire blade UP.
    shape.moveTo(r, 0);
    shape.lineTo(chord - r, 0);
    shape.quadraticCurveTo(chord, 0, chord, r);
    shape.lineTo(chord, 2 * hh - r);
    shape.quadraticCurveTo(chord, 2 * hh, chord - r, 2 * hh);
    shape.lineTo(r, 2 * hh);
    shape.quadraticCurveTo(0, 2 * hh, 0, 2 * hh - r);
    shape.lineTo(0, r);
    shape.quadraticCurveTo(0, 0, r, 0);

    const extrudeSettings = { steps: 1, depth: bladeSpan, bevelEnabled: false };
    const bladeGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    bladeGeo.translate(0, 0, -bladeSpan / 2);
    bladeGeo.rotateY(Math.PI / 2);

    // Blade pivot sits on the gutter lip. When fully open (vertical), blade top
    // is flush with the top of the beam assembly — per Azenco spec drawing.
    const bladeY = c.postHeight - chord;

    const usableLength = c.length - mbW * 2;

    // Blades are spaced so that when closed (0°), the chord of each blade
    // touches/slightly overlaps the next, creating visible seam lines.
    // The LAST blade (motor side, +Z) starts flush against the back gutter lip.
    // The FIRST blade may leave a gap at the front — filled by the fixed louver.
    const bladeSpacing = chord * 1.02; // slight overlap for visible seams when closed

    // Auto-calculate blade count to fill the usable length.
    // The fixed louver should never exceed one chord width (~9.5").
    const bladeCount = Math.max(1, Math.ceil(usableLength / bladeSpacing) - 1);
    const totalBladesLen = bladeCount * bladeSpacing;

    // Position blades starting from the back (+Z) end, working toward front (-Z).
    // After rotateY(PI/2), chord extends in -Z from the pivot. So pivot sits at
    // the back lip and chord reaches toward the front.
    const backLipZ = zCenter + halfL - mbW;
    const firstPivotZ = backLipZ; // rearmost blade pivot AT back gutter lip

    for (let i = 0; i < bladeCount; i++) {
      // i=0 is the blade nearest the back (motor side)
      const z = firstPivotZ - i * bladeSpacing;
      const blade = new THREE.Mesh(bladeGeo, this.materials.blade);
      blade.position.set(xCenter, bladeY, z);
      blade.castShadow = true;
      blade.receiveShadow = true;
      blade.userData.part = 'blade';
      this.group.add(blade);
      this.blades.push(blade);
    }

    // Fixed gap-closure louver — every zone gets one at its front (-Z) edge
    // to fill the remaining gap between the last movable blade and the front beam.
    {
      const frontLipZ = zCenter - halfL + mbW;
      const lastPivotZ = firstPivotZ - (bladeCount - 1) * bladeSpacing;
      // Last blade's far edge (chord extends in -Z from pivot)
      const lastBladeEnd = lastPivotZ - chord;
      const gapToFront = lastBladeEnd - frontLipZ;

      if (gapToFront > 0.5 * IN) {
        const fixedW = gapToFront;
        const fixedShape = new THREE.Shape();
        const fixedR = Math.min(fixedW, hh) * 0.3;
        fixedShape.moveTo(fixedR, 0);
        fixedShape.lineTo(fixedW - fixedR, 0);
        fixedShape.quadraticCurveTo(fixedW, 0, fixedW, fixedR);
        fixedShape.lineTo(fixedW, 2 * hh - fixedR);
        fixedShape.quadraticCurveTo(fixedW, 2 * hh, fixedW - fixedR, 2 * hh);
        fixedShape.lineTo(fixedR, 2 * hh);
        fixedShape.quadraticCurveTo(0, 2 * hh, 0, 2 * hh - fixedR);
        fixedShape.lineTo(0, fixedR);
        fixedShape.quadraticCurveTo(0, 0, fixedR, 0);

        const fixedGeo = new THREE.ExtrudeGeometry(fixedShape, { steps: 1, depth: bladeSpan, bevelEnabled: false });
        fixedGeo.translate(0, 0, -bladeSpan / 2);
        fixedGeo.rotateY(Math.PI / 2);

        // Pivot at last blade's far edge; fixed chord fills gap to front lip
        const fixedBlade = new THREE.Mesh(fixedGeo, this.materials.blade);
        fixedBlade.position.set(xCenter, bladeY, lastBladeEnd);
        fixedBlade.castShadow = true;
        fixedBlade.receiveShadow = true;
        fixedBlade.userData.part = 'fixed-blade';
        this.group.add(fixedBlade);
      }
    }

    this.setBladeAngle(this.bladeAngle);
  }

  // ─── R-Shade: Retractable fabric shade for a single zone ──────
  buildShadeForZone(c, zCenter) {
    const mbW = SPEC.MAIN_BEAM_FLANGE;
    const shadeW = c.width - mbW * 2;
    const shadeL = c.length - mbW * 2;
    const topOfPost = c.postHeight;
    const shadeY = topOfPost - SPEC.MAIN_BEAM_HEIGHT * 0.3;

    // Fabric plane with slight sag (subdivided for vertex displacement)
    const geo = new THREE.PlaneGeometry(shadeW, shadeL, 16, 16);
    const posAttr = geo.attributes.position;

    // Add slight catenary sag
    for (let i = 0; i < posAttr.count; i++) {
      const x = posAttr.getX(i);
      const y = posAttr.getY(i);
      // Normalized distances from center (0-1)
      const nx = (x / (shadeW / 2));
      const ny = (y / (shadeL / 2));
      // Parabolic sag: deepest at center
      const sag = (1 - nx * nx) * (1 - ny * ny) * 2 * IN;
      posAttr.setZ(i, -sag); // sag downward
    }
    posAttr.needsUpdate = true;
    geo.computeVertexNormals();

    const mesh = new THREE.Mesh(geo, this.materials.shadeFabric);
    mesh.position.set(0, shadeY, zCenter);
    mesh.rotation.x = -Math.PI / 2; // lay flat
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.part = 'shade-fabric';
    this.group.add(mesh);

    // Side guide rails (thin aluminum tracks the fabric slides on)
    const railH = 2 * IN;
    const railD = 1 * IN;
    const railGeo = new THREE.BoxGeometry(railD, railH, c.length - mbW * 2);
    const halfW = c.width / 2;

    this.addMesh(railGeo, this.materials.mainBeam,
      { x: -halfW + mbW + railD / 2, y: shadeY, z: zCenter }, 'shade-rail');
    this.addMesh(railGeo, this.materials.mainBeam,
      { x: halfW - mbW - railD / 2, y: shadeY, z: zCenter }, 'shade-rail');
  }

  // ─── R-Breeze: Fixed screen panels for a single zone ──────────
  buildBreezeForZone(c, zCenter) {
    const mbW = SPEC.MAIN_BEAM_FLANGE;
    const screenW = c.width - mbW * 2;
    const screenL = c.length - mbW * 2;
    const topOfPost = c.postHeight;
    const screenY = topOfPost - SPEC.MAIN_BEAM_HEIGHT * 0.5;
    const halfL = c.length / 2;

    // Horizontal slats (like fixed louvers)
    const slatCount = 12;
    const slatH = 1 * IN;
    const slatGap = screenL / (slatCount + 1);
    const slatGeo = new THREE.BoxGeometry(screenW, slatH, 1.5 * IN);

    for (let i = 0; i < slatCount; i++) {
      const z = zCenter - halfL + mbW + slatGap * (i + 1);
      this.addMesh(slatGeo, this.materials.breezeScreen,
        { x: 0, y: screenY, z }, 'breeze-slat');
    }

    // Cross members (2 running along length)
    const crossH = 1.5 * IN;
    const crossW = 1 * IN;
    const crossGeo = new THREE.BoxGeometry(crossW, crossH, screenL);
    const quarterW = screenW / 4;

    this.addMesh(crossGeo, this.materials.mainBeam,
      { x: -quarterW, y: screenY, z: zCenter }, 'breeze-cross');
    this.addMesh(crossGeo, this.materials.mainBeam,
      { x: quarterW, y: screenY, z: zCenter }, 'breeze-cross');
  }

  // ─── Junction beams between adjacent zones ────────────────────
  buildJunctionBeams(zones, offsets) {
    const beamH = SPEC.MAIN_BEAM_HEIGHT;
    const beamW = SPEC.MAIN_BEAM_FLANGE;
    // Full width so junction covers side beam areas too — no gap/dark band
    const fullW = this.config.width;

    for (let i = 0; i < zones.length - 1; i++) {
      const jZ = offsets[i].endZ * IN;
      const beamCenterY = this.config.postHeight - beamH / 2;

      // Two full beams sandwiched: end of zone i meets start of zone i+1
      const beam1Z = jZ - beamW / 2;
      const beam2Z = jZ + beamW / 2;

      this.addMesh(new THREE.BoxGeometry(fullW, beamH, beamW), this.materials.mainBeam,
        { x: 0, y: beamCenterY, z: beam1Z }, 'junction-beam');
      this.addMesh(new THREE.BoxGeometry(fullW, beamH, beamW), this.materials.mainBeam,
        { x: 0, y: beamCenterY, z: beam2Z }, 'junction-beam');
    }
  }

  // ─── Junction beams between width-axis zones (run along Z) ──────
  buildWidthJunctionBeams(zones, offsets) {
    const beamH = SPEC.MAIN_BEAM_HEIGHT;
    const beamW = SPEC.MAIN_BEAM_FLANGE;
    const fullL = this.zoneManager.getTotalLength() * IN;

    for (let i = 0; i < zones.length - 1; i++) {
      const jX = offsets[i].endX * IN;
      const beamCenterY = this.config.postHeight - beamH / 2;

      const beam1X = jX - beamW / 2;
      const beam2X = jX + beamW / 2;

      this.addMesh(new THREE.BoxGeometry(beamW, beamH, fullL), this.materials.mainBeam,
        { x: beam1X, y: beamCenterY, z: 0 }, 'junction-beam');
      this.addMesh(new THREE.BoxGeometry(beamW, beamH, fullL), this.materials.mainBeam,
        { x: beam2X, y: beamCenterY, z: 0 }, 'junction-beam');
    }
  }

  // ─── Edge trim for a single zone ───────────────────────────────
  buildZoneEdgeTrim(c, zCenter, isFirst, isLast) {
    const eh = SPEC.EDGE_HEADER_H;
    const ed = SPEC.EDGE_HEADER_D;
    const halfW = c.width / 2;
    const halfL = c.length / 2;
    const topY = c.postHeight;

    // Side trim runs the zone's length
    const trimSideGeo = new THREE.BoxGeometry(ed, eh, c.length + (isFirst ? ed : 0) + (isLast ? ed : 0));
    const sideZOffset = zCenter + ((isFirst ? -ed : 0) + (isLast ? ed : 0)) / 2;

    this.addMesh(trimSideGeo, this.materials.edgeTrim,
      { x: -halfW - ed / 2, y: topY + eh / 2, z: sideZOffset }, 'trim');
    this.addMesh(trimSideGeo, this.materials.edgeTrim,
      { x: halfW + ed / 2, y: topY + eh / 2, z: sideZOffset }, 'trim');

    // Front/back trim only on outer edges
    const trimFrontGeo = new THREE.BoxGeometry(c.width + ed * 2, eh, ed);

    if (isFirst) {
      this.addMesh(trimFrontGeo, this.materials.edgeTrim,
        { x: 0, y: topY + eh / 2, z: zCenter - halfL - ed / 2 }, 'trim');
    }
    if (isLast) {
      this.addMesh(trimFrontGeo, this.materials.edgeTrim,
        { x: 0, y: topY + eh / 2, z: zCenter + halfL + ed / 2 }, 'trim');
    }
  }

  // ─── Custom Beams (fan beam, structural, heater, reinforcement) ──
  buildCustomBeams(c) {
    const beams = this.beamManager.getBeams();
    const mbW = SPEC.MAIN_BEAM_FLANGE;
    const halfW = c.width / 2;
    const halfL = c.length / 2;
    const topOfPost = c.postHeight;
    const zones = this.zoneManager.getZones();
    const offsets = this.zoneManager.getZoneOffsets();
    const isMultiZone = zones.length > 1;

    for (const beam of beams) {
      const spec = BEAM_TYPES[beam.type];
      if (!spec) continue;

      const bw = spec.widthIn * IN;
      const bh = spec.heightIn * IN;
      const matKey = 'beam' + beam.type.charAt(0).toUpperCase() + beam.type.slice(1);
      const mat = this.materials[matKey] || this.materials.mainBeam;

      // Beam Y: bottom of beam aligns with bottom of main beam (fascia underside)
      const beamBottomY = topOfPost - SPEC.MAIN_BEAM_HEIGHT;
      const beamY = beamBottomY + bh / 2;

      // Fan beams (accessory): one per zone, centered in each zone
      if (beam.type === 'accessory' && isMultiZone && beam.axis === 'width') {
        const span = c.width - mbW * 2;
        const geo = new THREE.BoxGeometry(span, bh, bw);
        for (const offset of offsets) {
          const zoneZ = offset.centerZ * IN;
          this.addMesh(geo, mat, { x: 0, y: beamY, z: zoneZ }, 'beam-' + beam.type);
        }
        continue;
      }

      if (beam.axis === 'width') {
        // Beam runs along X (width), positioned at Z offset
        const span = c.width - mbW * 2;
        const geo = new THREE.BoxGeometry(span, bh, bw);
        const z = beam.positionIn * IN;
        this.addMesh(geo, mat, { x: 0, y: beamY, z }, 'beam-' + beam.type);
      } else {
        // Beam runs along Z (length), positioned at X offset
        const span = c.length - mbW * 2;
        const geo = new THREE.BoxGeometry(bw, bh, span);
        const x = beam.positionIn * IN;
        this.addMesh(geo, mat, { x, y: beamY, z: 0 }, 'beam-' + beam.type);
      }

      // Heater beam: add vent slots visual
      if (beam.type === 'heater') {
        const ventCount = 5;
        const ventLen = 2 * IN;
        const ventH = 0.5 * IN;
        const ventGap = bw / (ventCount + 1);
        const ventGeo = new THREE.BoxGeometry(ventLen, ventH, 0.5 * IN);
        const ventMat = new THREE.MeshStandardMaterial({
          color: 0x1a1a1a, roughness: 0.9, metalness: 0.1,
        });
        for (let i = 0; i < ventCount; i++) {
          const offset = (i + 1) * ventGap - bw / 2;
          if (beam.axis === 'width') {
            this.addMesh(ventGeo, ventMat,
              { x: offset / IN * IN * 8, y: beamY - bh / 2 - ventH / 2, z: beam.positionIn * IN },
              'heater-vent');
          }
        }
      }
    }
  }

  // ─── Lighting (recessed, LED bars, LED strips, solar, fans) ────
  buildLighting(c) {
    const effective = this.lightingManager.getEffectiveCounts();
    const halfW = c.width / 2;
    const halfL = c.length / 2;
    const mbW = SPEC.MAIN_BEAM_FLANGE;
    const topOfPost = c.postHeight;
    const undersideY = topOfPost - SPEC.MAIN_BEAM_HEIGHT;

    const fanBeam = this.beamManager.getBeams().find(b => b.type === 'accessory');

    // In multi-zone, fans/recessed/LED bars repeat per zone
    const zones = this.zoneManager.getZones();
    const offsets = this.zoneManager.getZoneOffsets();
    const isMultiZone = zones.length > 1;
    const isWidthAxis = this.zoneManager.isWidthAxis();

    // Per-zone lighting slots: { xCenter, zCenter, zoneWidth, zoneLength }
    const zoneLightSlots = [];
    if (isMultiZone && isWidthAxis) {
      for (const o of offsets) {
        zoneLightSlots.push({
          xCenter: o.centerX * IN,
          zCenter: 0,
          zoneWidth: o.widthInches * IN,
          zoneLength: c.length,
        });
      }
    } else if (isMultiZone) {
      for (const o of offsets) {
        zoneLightSlots.push({
          xCenter: 0,
          zCenter: o.centerZ * IN,
          zoneWidth: c.width,
          zoneLength: o.lengthInches * IN,
        });
      }
    } else {
      zoneLightSlots.push({
        xCenter: 0,
        zCenter: fanBeam ? fanBeam.positionIn * IN : 0,
        zoneWidth: c.width,
        zoneLength: c.length,
      });
    }

    this._lightingWarnings = this._lightingWarnings || [];
    if (!fanBeam && (effective.ceilingFan > 0 || effective.recessed > 0)) {
      if (!this._lightingWarnings.includes('no-fan-beam')) {
        this._lightingWarnings.push('no-fan-beam');
      }
    } else {
      this._lightingWarnings = this._lightingWarnings.filter(w => w !== 'no-fan-beam');
    }

    // ── Recessed lights: on the fan beam, flanking the fan — per zone ──
    if (effective.recessed > 0) {
      const lightSize = 3 * IN;
      const lightDepth = 1.5 * IN;
      const geo = new THREE.CylinderGeometry(lightSize / 2, lightSize / 2, lightDepth, 16);
      const meshY = undersideY + lightDepth / 2;

      for (const slot of zoneLightSlots) {
        const beamEndLeft  = slot.xCenter - (slot.zoneWidth / 2 - mbW);
        const beamEndRight = slot.xCenter + (slot.zoneWidth / 2 - mbW);
        const recessedX1 = (slot.xCenter + beamEndLeft) / 2;
        const recessedX2 = (slot.xCenter + beamEndRight) / 2;

        for (let i = 0; i < Math.min(effective.recessed, 2); i++) {
          const xOff = i === 0 ? recessedX1 : recessedX2;
          this.addMesh(geo, this.materials.recessedLight,
            { x: xOff, y: meshY, z: slot.zCenter }, 'recessed-light');

          const pointLight = new THREE.PointLight(0xfff0b0, 0.6, slot.zoneWidth * 0.6);
          pointLight.position.set(xOff, meshY, slot.zCenter);
          this.scene.add(pointLight);
          this.lightObjects.push(pointLight);
        }
      }
    }

    // ── LED Bar / Ramp lights: 4 per zone — run PARALLEL to side beams (along Z) ──
    if (effective.ledBar > 0) {
      const barLen = 24 * IN;   // 2' long bars
      const barH = 1 * IN;
      const barD = 2 * IN;
      const geo = new THREE.BoxGeometry(barD, barH, barLen);

      for (const slot of zoneLightSlots) {
        const zHalfW = slot.zoneWidth / 2;
        const positions = [
          { x: slot.xCenter - zHalfW + mbW / 2, z: slot.zCenter - slot.zoneLength * 0.25 },
          { x: slot.xCenter - zHalfW + mbW / 2, z: slot.zCenter + slot.zoneLength * 0.25 },
          { x: slot.xCenter + zHalfW - mbW / 2, z: slot.zCenter - slot.zoneLength * 0.25 },
          { x: slot.xCenter + zHalfW - mbW / 2, z: slot.zCenter + slot.zoneLength * 0.25 },
        ];

        for (let i = 0; i < Math.min(effective.ledBar, positions.length); i++) {
          const pos = positions[i];
          const barY = undersideY - barH / 2;
          this.addMesh(geo, this.materials.ledBar,
            { x: pos.x, y: barY, z: pos.z }, 'led-bar');

          const light = new THREE.PointLight(0xffffff, 0.3, slot.zoneWidth * 0.5);
          light.position.set(pos.x, barY, pos.z);
          this.scene.add(light);
          this.lightObjects.push(light);
        }
      }
    }

    // ── LED Strip lights: perimeter ambient glow on louver undersides ──
    if (effective.ledStrip) {
      const stripH = 0.5 * IN;
      const stripD = 1 * IN;

      // HSL color from hue
      const hue = effective.ledStripColorHue / 360;
      const brightness = effective.ledStripBrightness;
      const stripColor = new THREE.Color().setHSL(hue, 0.9, 0.5 * brightness + 0.2);

      const stripMat = this.materials.ledStrip.clone();
      stripMat.emissive.copy(stripColor);
      stripMat.color.copy(stripColor);
      stripMat.emissiveIntensity = brightness;

      // 4 strip segments along perimeter (inside the fascia)
      const innerW = c.width - mbW * 2;
      const innerL = c.length - mbW * 2;
      const stripY = undersideY + stripH / 2;

      // Left strip
      const leftGeo = new THREE.BoxGeometry(stripD, stripH, innerL);
      this.addMesh(leftGeo, stripMat,
        { x: -halfW + mbW + stripD / 2, y: stripY, z: 0 }, 'led-strip');
      // Right strip
      this.addMesh(leftGeo.clone(), stripMat,
        { x: halfW - mbW - stripD / 2, y: stripY, z: 0 }, 'led-strip');
      // Front strip
      const frontGeo = new THREE.BoxGeometry(innerW, stripH, stripD);
      this.addMesh(frontGeo, stripMat,
        { x: 0, y: stripY, z: -halfL + mbW + stripD / 2 }, 'led-strip');
      // Back strip
      this.addMesh(frontGeo.clone(), stripMat,
        { x: 0, y: stripY, z: halfL - mbW - stripD / 2 }, 'led-strip');

      // Ambient glow lights at corners for color wash effect on louver undersides
      const glowIntensity = brightness * 0.8;
      const glowDist = 3;
      const cornerPositions = [
        { x: -halfW + mbW, z: -halfL + mbW },
        { x: halfW - mbW, z: -halfL + mbW },
        { x: halfW - mbW, z: halfL - mbW },
        { x: -halfW + mbW, z: halfL - mbW },
      ];
      for (const cp of cornerPositions) {
        const glow = new THREE.PointLight(stripColor.getHex(), glowIntensity, glowDist);
        glow.position.set(cp.x, stripY, cp.z);
        this.scene.add(glow);
        this.lightObjects.push(glow);
      }
    }

    // ── Solar post lights: mounted just under beam, on top of post ──
    // solarPostLights = per-post count; place up to that many lights on each post's outer faces
    if (effective.solarPostLights > 0) {
      const solarSize = 2 * IN;
      const solarH = 3 * IN;
      const geo = new THREE.CylinderGeometry(solarSize / 2, solarSize / 2, solarH, 8);
      const ps = SPEC.POST_SIZE;
      const lightTopY = topOfPost - SPEC.MAIN_BEAM_HEIGHT;
      const lightCenterY = lightTopY - solarH / 2;
      const perPost = Math.min(effective.solarPostLights, 4);

      for (const postPos of this.wallManager.postPositions) {
        const px = postPos.x * IN;
        const pz = postPos.z * IN;
        const xSign = postPos.x < 0 ? 1 : postPos.x > 0 ? -1 : 0;
        const zSign = postPos.z < 0 ? 1 : postPos.z > 0 ? -1 : 0;
        const adjX = px + (postPos.type === 'corner' ? xSign * ps / 2 : 0);
        const adjZ = pz + (postPos.type === 'corner' ? zSign * ps / 2 : 0);

        const faceOffsets = [
          { dx: 0, dz: -ps / 2 - solarSize / 2 },
          { dx: 0, dz: ps / 2 + solarSize / 2 },
          { dx: -ps / 2 - solarSize / 2, dz: 0 },
          { dx: ps / 2 + solarSize / 2, dz: 0 },
        ];

        for (let i = 0; i < perPost; i++) {
          const off = faceOffsets[i];
          this.addMesh(geo, this.materials.solarLight,
            { x: adjX + off.dx, y: lightCenterY, z: adjZ + off.dz }, 'solar-light');

          const downLight = new THREE.SpotLight(0xfff5cc, 0.5, c.postHeight * 0.8, Math.PI / 3, 0.5);
          downLight.position.set(adjX + off.dx, lightCenterY - solarH / 2, adjZ + off.dz);
          downLight.target.position.set(adjX + off.dx, 0, adjZ + off.dz);
          this.scene.add(downLight);
          this.scene.add(downLight.target);
          this.lightObjects.push(downLight);
          this.lightObjects.push(downLight.target);
        }
      }
    }

    // ── Ceiling fan: Minka-Aire Skinnie 56" 3-blade style — per zone ──
    if (effective.ceilingFan > 0) {
      const bladeSweep = 56 * IN;
      const bladeLen = bladeSweep / 2;
      const bladeW = 3.5 * IN;
      const bladeH = 0.2 * IN;
      const hubRadius = 2.5 * IN;
      const hubHeight = 3 * IN;
      const rodLen = 6 * IN;
      const rodRadius = 0.375 * IN;
      const lightRadius = 2 * IN;
      const lightDepth = 1 * IN;

      const canopyGeo = new THREE.CylinderGeometry(2 * IN, 2 * IN, 0.5 * IN, 16);
      const rodGeo = new THREE.CylinderGeometry(rodRadius, rodRadius, rodLen, 8);
      const hubGeo = new THREE.CylinderGeometry(hubRadius, hubRadius * 0.7, hubHeight, 24);
      const lightGeo = new THREE.CylinderGeometry(lightRadius, lightRadius, lightDepth, 16);

      const fanY = undersideY - rodLen - hubHeight;

      for (const slot of zoneLightSlots) {
        const fx = slot.xCenter;
        const fz = slot.zCenter;

        this.addMesh(canopyGeo, this.materials.fanHub,
          { x: fx, y: undersideY - 0.25 * IN, z: fz }, 'fan-canopy');
        this.addMesh(rodGeo, this.materials.fanHub,
          { x: fx, y: undersideY - rodLen / 2, z: fz }, 'fan-rod');
        this.addMesh(hubGeo, this.materials.fanHub,
          { x: fx, y: undersideY - rodLen - hubHeight / 2, z: fz }, 'fan-hub');

        const fanLightY = fanY - lightDepth / 2;
        this.addMesh(lightGeo, this.materials.recessedLight,
          { x: fx, y: fanLightY, z: fz }, 'fan-light');

        const fanLight = new THREE.PointLight(0xfff0b0, 0.5, slot.zoneWidth * 0.6);
        fanLight.position.set(fx, fanLightY, fz);
        this.scene.add(fanLight);
        this.lightObjects.push(fanLight);

        for (let b = 0; b < 3; b++) {
          const angle = (b / 3) * Math.PI * 2;
          const bladeShape = new THREE.Shape();
          bladeShape.moveTo(hubRadius * 0.8, -bladeW * 0.2);
          bladeShape.lineTo(bladeLen * 0.2, -bladeW * 0.45);
          bladeShape.lineTo(bladeLen * 0.5, -bladeW * 0.5);
          bladeShape.lineTo(bladeLen * 0.85, -bladeW * 0.35);
          bladeShape.lineTo(bladeLen, -bladeW * 0.08);
          bladeShape.quadraticCurveTo(bladeLen + bladeW * 0.05, 0, bladeLen, bladeW * 0.08);
          bladeShape.lineTo(bladeLen * 0.85, bladeW * 0.35);
          bladeShape.lineTo(bladeLen * 0.5, bladeW * 0.5);
          bladeShape.lineTo(bladeLen * 0.2, bladeW * 0.45);
          bladeShape.lineTo(hubRadius * 0.8, bladeW * 0.2);
          bladeShape.closePath();

          const bladeMeshGeo = new THREE.ExtrudeGeometry(bladeShape, {
            depth: bladeH, bevelEnabled: false,
          });
          bladeMeshGeo.rotateX(-Math.PI / 2);
          bladeMeshGeo.translate(0, bladeH / 2, 0);

          const mesh = new THREE.Mesh(bladeMeshGeo, this.materials.fanBlade);
          mesh.position.set(fx, fanY + hubHeight * 0.5, fz);
          mesh.rotation.y = angle;
          mesh.userData.part = 'fan-blade';
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          this.group.add(mesh);
        }
      }
    }
  }

  // ─── Architecture (corbel ends, cornice, cornice LED) ──────────
  buildArchitecture(c) {
    const halfW = c.width / 2;
    const halfL = c.length / 2;
    const topOfPost = c.postHeight;

    // ── Corbel ends: decorative brackets at post-to-beam junctions ──
    if (c.corbelEnds) {
      const corbelLen = 8 * IN;
      const corbelH = 8 * IN;
      const corbelThick = 1.5 * IN;

      for (const pos of this.wallManager.postPositions) {
        const xSign = pos.x < 0 ? 1 : pos.x > 0 ? -1 : 0;
        const zSign = pos.z < 0 ? 1 : pos.z > 0 ? -1 : 0;
        const ps = SPEC.POST_SIZE;
        const px = pos.x * IN + (pos.type === 'corner' ? xSign * ps / 2 : 0);
        const pz = pos.z * IN + (pos.type === 'corner' ? zSign * ps / 2 : 0);
        const corbelY = topOfPost - SPEC.MAIN_BEAM_HEIGHT - corbelH / 2;

        // Triangular corbel shape (simplified as rotated wedge box)
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(corbelLen, 0);
        shape.lineTo(0, corbelH);
        shape.closePath();

        const extrudeGeo = new THREE.ExtrudeGeometry(shape, {
          depth: corbelThick, bevelEnabled: false,
        });
        extrudeGeo.translate(-corbelLen / 2, -corbelH / 2, -corbelThick / 2);

        // Place 2 corbels per post (one on each open side)
        if (pos.type === 'corner') {
          // Z-facing corbel
          const m1 = new THREE.Mesh(extrudeGeo, this.materials.corbel);
          m1.position.set(px, corbelY, pz - zSign * ps / 2);
          m1.rotation.y = zSign > 0 ? 0 : Math.PI;
          m1.castShadow = true;
          this.group.add(m1);

          // X-facing corbel
          const m2 = new THREE.Mesh(extrudeGeo.clone(), this.materials.corbel);
          m2.position.set(px - xSign * ps / 2, corbelY, pz);
          m2.rotation.y = xSign > 0 ? -Math.PI / 2 : Math.PI / 2;
          m2.castShadow = true;
          this.group.add(m2);
        }
      }
    }

    // NOTE: Crown molding / cornice removed — beam tops are flat surfaces per reference design
  }

  // ─── Progressive Magnatrack Retractable Shades ───────────────
  buildShades(c) {
    if (!c.shades) return;

    const { mountType, sides } = c.shades;
    const halfW  = c.width / 2;
    const halfL  = c.length / 2;
    const beamH  = SPEC.MAIN_BEAM_HEIGHT;   // 14" — full fascia height
    const ps     = SPEC.POST_SIZE;           // 6.5"

    const HOUSING  = 7.5 * IN;   // 7.5" × 7.5" housing cross-section
    const TRACK    = 0.75 * IN;  // 0.75" × 0.75" side track
    const BAR_H    = 1.5 * IN;   // weight bar height
    const BAR_D    = 1.5 * IN;   // weight bar depth


    // Housing Y center and bottom, differ by mount type.
    // IUH (inside): cassette hangs from beam underside, flush under beam bottom.
    // OAH (outside): cassette sits at beam-top height on the outer face — not above it.
    const housingCenterY = mountType === 'inside'
      ? c.postHeight - beamH - HOUSING / 2   // top of housing = beam bottom
      : c.postHeight - HOUSING / 2;           // top of housing = beam top (outer face)
    const housingBottomY = housingCenterY - HOUSING / 2;

    // Shade height = vertical distance the screen can travel (housing bottom → floor)
    const shadeHeight = housingBottomY;

    for (const side of ['front', 'back', 'left', 'right']) {
      const shade = sides[side];
      if (!shade.enabled || c.walls[side]) continue;

      const isFront = side === 'front';
      const isBack  = side === 'back';

      // front/back run along X; left/right run along Z
      const openingW = (isFront || isBack) ? c.width : c.length;

      // ── Housing position ────────────────────────────────────────
      let housingX = 0, housingZ = 0;

      if (isFront || isBack) {
        const zSign = isFront ? -1 : +1;
        housingZ = mountType === 'inside'
          ? zSign * (halfL - HOUSING / 2)   // front face flush with fascia outer face
          : zSign * (halfL + HOUSING / 2);  // housing sits outside the fascia (OAH)
      } else {
        const xSign = (side === 'left') ? -1 : +1;
        housingX = mountType === 'inside'
          ? xSign * (halfW - HOUSING / 2)   // outer face flush with side fascia outer face
          : xSign * (halfW + HOUSING / 2);  // housing sits outside the fascia (OAH)
      }

      // ── Housing box ─────────────────────────────────────────────
      const hGeo = (isFront || isBack)
        ? new THREE.BoxGeometry(openingW, HOUSING, HOUSING)
        : new THREE.BoxGeometry(HOUSING, HOUSING, openingW);
      const hMesh = new THREE.Mesh(hGeo, this.materials.shadeHousing);
      hMesh.position.set(housingX, housingCenterY, housingZ);
      hMesh.userData.part = 'shade-housing';
      this.group.add(hMesh);

      // ── Side tracks ─────────────────────────────────────────────
      const trackH  = housingBottomY;
      const trackY  = trackH / 2;
      const trackGeo = new THREE.BoxGeometry(TRACK, trackH, TRACK);

      if (isFront || isBack) {
        const trackZ = housingZ;
        // IUH: track on inside face of side post; OAH: track on outside face of side post
        const tx = mountType === 'inside'
          ? halfW - ps - TRACK / 2
          : halfW + TRACK / 2;
        [-tx, +tx].forEach(x => {
          const t = new THREE.Mesh(trackGeo, this.materials.shadeHousing);
          t.position.set(x, trackY, trackZ);
          t.userData.part = 'shade-track';
          this.group.add(t);
        });
      } else {
        const trackX = housingX;
        // IUH: track on inside face of front/back post; OAH: outside face
        const tz = mountType === 'inside'
          ? halfL - ps - TRACK / 2
          : halfL + TRACK / 2;
        [-tz, +tz].forEach(z => {
          const t = new THREE.Mesh(trackGeo, this.materials.shadeHousing);
          t.position.set(trackX, trackY, z);
          t.userData.part = 'shade-track';
          this.group.add(t);
        });
      }

      // ── Screen mesh + weight bar (only when position > 0) ───────
      if (shade.position > 0) {
        const deployedH = shadeHeight * shade.position;
        const meshCenterY = housingBottomY - deployedH / 2;

        // PlaneGeometry in XY plane, facing +Z by default
        // front/back shades: no rotation needed (plane naturally faces ±Z)
        // left/right shades: rotate Y 90° to face ±X
        const mGeo = new THREE.PlaneGeometry(openingW, deployedH);
        const mMesh = new THREE.Mesh(mGeo, this.materials.shadeMesh);

        if (isFront || isBack) {
          mMesh.position.set(0, meshCenterY, housingZ);
        } else {
          mMesh.position.set(housingX, meshCenterY, 0);
          mMesh.rotation.y = Math.PI / 2;
        }
        mMesh.castShadow = true;
        mMesh.receiveShadow = true;
        mMesh.renderOrder = 1;
        mMesh.userData.part = 'shade-mesh';
        this.group.add(mMesh);

        // Weight bar at bottom of deployed screen
        const barCenterY = housingBottomY - deployedH - BAR_H / 2;
        const barGeo = (isFront || isBack)
          ? new THREE.BoxGeometry(openingW, BAR_H, BAR_D)
          : new THREE.BoxGeometry(BAR_D, BAR_H, openingW);
        const barMesh = new THREE.Mesh(barGeo, this.materials.shadeHousing);
        barMesh.position.set(
          (isFront || isBack) ? 0 : housingX,
          barCenterY,
          (isFront || isBack) ? housingZ : 0
        );
        barMesh.userData.part = 'shade-weightbar';
        this.group.add(barMesh);
      }
    }
  }

  // ─── Animated roof open/close ─────────────────────────────────
  animateRoofTo(targetDegrees, duration = 1500) {
    if (this.roofAnimating) return;
    this.roofAnimating = true;
    const startAngle = this.bladeAngle;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease in-out
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const current = startAngle + (targetDegrees - startAngle) * eased;
      this.setBladeAngle(current);

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        this.roofAnimating = false;
        this.bladeAngle = targetDegrees;
      }
    };
    requestAnimationFrame(animate);
  }

  closeRoof() {
    this.animateRoofTo(0);
  }

  openRoof() {
    this.animateRoofTo(90);
  }

  // ─── Architecture controls ────────────────────────────────────
  setCorbelEnds(enabled) {
    this.config.corbelEnds = enabled;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setCorniceStyle(style) {
    this.config.corniceStyle = style;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setCorniceLed(enabled) {
    this.config.corniceLed = enabled;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setFloorType(type) {
    this.config.floorType = type; // 'wood', 'stone', or 'grass'
  }

  getFloorMaterial() {
    if (this.config.floorType === 'stone') return this.materials.stonePatio;
    if (this.config.floorType === 'grass') return this.materials.grass;
    return this.materials.woodDecking;
  }

  // ─── Controls ──────────────────────────────────────────────────

  setBladeAngle(degrees) {
    // Azenco R-Blade: max 90° open from closed position.
    // Blades always open UPWARD (toward the sky), away from the fixed louver.
    // 0° = closed (flat), 90° = fully open.
    this.bladeAngle = Math.max(0, Math.min(90, degrees));
    const radians = THREE.MathUtils.degToRad(this.bladeAngle);
    for (const blade of this.blades) {
      // Positive rotation.x swings the blade chord upward toward the sky
      blade.rotation.x = radians;
    }
  }

  setWall(side, enabled) {
    this.config.walls[side] = enabled;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  // ─── Post API ─────────────────────────────────────────────────
  addUserPost(xInches, zInches, label) {
    const post = this.wallManager.addUserPost(xInches, zInches, label);
    this.build();
    this.setBladeAngle(this.bladeAngle);
    return post;
  }

  removeUserPost(id) {
    this.wallManager.removeUserPost(id);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setCantilever(cornerIndex, inchesInset) {
    this.wallManager.setCantilever(cornerIndex, inchesInset);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  getCantilever(cornerIndex) {
    return this.wallManager.getCantilever(cornerIndex);
  }

  setCenterPostToggle(axis, enabled) {
    this.wallManager.setCenterPostToggle(axis, enabled);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  getCenterPostToggles() {
    return this.wallManager.getCenterPostToggles();
  }

  setHasStructuralHeader(hasHeader) {
    this.wallManager.setHasStructuralHeader(hasHeader);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  getPostWarnings() {
    return this.wallManager.getWarnings();
  }

  getRemovablePosts() {
    return this.wallManager.getRemovablePosts();
  }

  // ─── Beam API ─────────────────────────────────────────────────
  addBeam(type, axis, positionIn) {
    const beam = this.beamManager.addBeam(type, axis, positionIn);
    this.build();
    this.setBladeAngle(this.bladeAngle);
    return beam;
  }

  removeBeam(id) {
    this.beamManager.removeBeam(id);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  moveBeam(id, positionIn) {
    const widthIn = this.config.width / IN;
    const lengthIn = this.config.length / IN;
    const beam = this.beamManager.beams.find(b => b.id === id);
    if (!beam) return;
    // Snap logic
    const snap = this.beamManager.snapPosition(positionIn, widthIn, lengthIn, beam.axis);
    this.beamManager.moveBeam(id, snap.positionIn);
    this.build();
    this.setBladeAngle(this.bladeAngle);
    return snap;
  }

  snapBeamToCenter(id) {
    this.beamManager.moveBeam(id, 0);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  // ─── Lighting API ────────────────────────────────────────────
  setLighting(key, value) {
    const lm = this.lightingManager;
    switch (key) {
      case 'recessed': lm.setRecessed(value); break;
      case 'ledBar': lm.setLedBars(value); break;
      case 'ledStrip': lm.setLedStrip(value); break;
      case 'ledStripColor': lm.setLedStripColor(value); break;
      case 'ledStripBrightness': lm.setLedStripBrightness(value); break;
      case 'solarPostLights': lm.setSolarPostLights(value); break;
      case 'ceilingFan': lm.setCeilingFan(value); break;
      case 'lightsAndFanPackage': lm.setLightsAndFanPackage(value); break;
    }
    this.build();
    this.setBladeAngle(this.bladeAngle);
    return lm.validate();
  }

  getLightingWarnings() {
    const result = this.lightingManager.validate();
    // Append geometry-level warnings (no fan beam for fan/recessed)
    if (this._lightingWarnings && this._lightingWarnings.includes('no-fan-beam')) {
      result.warnings.push('No Fan Beam added — fan and recessed lights default to center. Add a Fan Beam for precise placement.');
    }
    return result;
  }

  setDimensions(widthIn, lengthIn, heightIn) {
    this.config.width = Math.min(widthIn * IN, SPEC.MAX_WIDTH);
    this.config.length = Math.min(lengthIn * IN, SPEC.MAX_LENGTH);
    this.config.postHeight = heightIn * IN;
    // Sync first zone if single-zone mode
    const zones = this.zoneManager.getZones();
    if (zones.length === 1) {
      this.zoneManager.updateZone(zones[0].id, {
        lengthInches: lengthIn,
      });
    }
    this.zoneManager.setSharedWidth(widthIn);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setPostHeight(heightIn) {
    this.config.postHeight = heightIn * IN;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  // Update shared width across all zones (linear_length mode)
  setSharedWidth(widthIn) {
    this.config.width = widthIn * IN;
    this.zoneManager.setSharedWidth(widthIn);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  // Update shared length across all zones (linear_width mode)
  setSharedLength(lengthIn) {
    this.config.length = lengthIn * IN;
    this.zoneManager.setSharedLength(lengthIn);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  // ─── Zone API ──────────────────────────────────────────────────

  addZone(roofType) {
    const zone = this.zoneManager.addZone(roofType);
    this.build();
    this.setBladeAngle(this.bladeAngle);
    return zone;
  }

  removeZone(id) {
    const removed = this.zoneManager.removeZone(id);
    if (removed) {
      this.build();
      this.setBladeAngle(this.bladeAngle);
    }
    return removed;
  }

  updateZone(id, props) {
    const zone = this.zoneManager.updateZone(id, props);
    if (zone) {
      this.build();
      this.setBladeAngle(this.bladeAngle);
    }
    return zone;
  }

  setZoneRoofType(zoneId, roofType) {
    return this.updateZone(zoneId, { roofType });
  }

  getZones() {
    return this.zoneManager.getZones();
  }

  getZoneManager() {
    return this.zoneManager;
  }

  setZoneAxis(axis) {
    const mode = axis === 'width' ? 'linear_width' : 'linear_length';
    this.zoneManager.setLayoutMode(mode);
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  setColor(part, color) {
    const hex = new THREE.Color(color);
    if (part === 'post') {
      this.materials.post.color.copy(hex);
      this.config.postColor = hex;
      if (this.materials.shadeHousing) this.materials.shadeHousing.color.copy(hex);
    }
    if (part === 'beam' || part === 'all') {
      this.materials.mainBeam.color.copy(hex);
      this.materials.topBeam.color.copy(hex);
      this.materials.connector.color.copy(hex);
      this.materials.edgeTrim.color.copy(hex);
      this.config.beamColor = hex;
    }
    if (part === 'blade') {
      // Only update flat color if wood grain is not active
      if (!this._bladeWoodGrain) {
        this.materials.blade.color.copy(hex);
        this.config.bladeColor = hex;
      }
    }
    if (part === 'all') {
      this.materials.post.color.copy(hex);
      if (!this._bladeWoodGrain) {
        this.materials.blade.color.copy(hex);
        this.config.bladeColor = hex;
      }
      this.config.postColor = hex;
      if (this.materials.shadeHousing) this.materials.shadeHousing.color.copy(hex);
    }
  }

  setBladeWoodGrain(enabled) {
    this._bladeWoodGrain = enabled;
    if (enabled) {
      if (!this._woodGrainBladeTexture) {
        this._woodGrainBladeTexture = this._createBladeWoodGrainTexture();
      }
      this.materials.blade.map = this._woodGrainBladeTexture;
      this.materials.blade.color.set(0xffffff); // let texture drive color
      this.materials.blade.roughness = 0.55;
      this.materials.blade.metalness = 0.0;
      this.materials.blade.envMapIntensity = 0.2;
    } else {
      this.materials.blade.map = null;
      this.materials.blade.color.set(this.config.bladeColor);
      this.materials.blade.roughness = 0.32;
      this.materials.blade.metalness = 0.85;
      this.materials.blade.envMapIntensity = 1.0;
    }
    this.materials.blade.needsUpdate = true;
    this.build();
    this.setBladeAngle(this.bladeAngle);
  }

  _createBladeWoodGrainTexture() {
    // Warm cherry/honey wood grain — grain runs along blade length (x-axis)
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');

    // Base warm honey color
    ctx.fillStyle = '#c8823e';
    ctx.fillRect(0, 0, 512, 128);

    // Subtle base variation — lighter center, darker edges (wood profile)
    const grad = ctx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0,    'rgba(160,95,30,0.3)');
    grad.addColorStop(0.15, 'rgba(210,140,60,0.1)');
    grad.addColorStop(0.5,  'rgba(230,165,80,0.12)');
    grad.addColorStop(0.85, 'rgba(210,140,60,0.1)');
    grad.addColorStop(1,    'rgba(160,95,30,0.3)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 128);

    // Fine grain lines running along x-axis (blade length direction)
    const grainPalette = [
      'rgba(100,55,15,VAL)', 'rgba(130,70,20,VAL)',
      'rgba(90,48,12,VAL)',  'rgba(155,85,28,VAL)',
      'rgba(80,42,10,VAL)',
    ];
    const lineCount = 80;
    for (let i = 0; i < lineCount; i++) {
      const y = (i / lineCount) * 128 + (Math.random() - 0.5) * 3;
      const alpha = (0.15 + Math.random() * 0.35).toFixed(2);
      const c = grainPalette[Math.floor(Math.random() * grainPalette.length)].replace('VAL', alpha);
      ctx.strokeStyle = c;
      ctx.lineWidth = 0.4 + Math.random() * 1.2;
      ctx.beginPath();
      ctx.moveTo(0, y);
      let cx = 0;
      while (cx < 512) {
        cx += 6 + Math.random() * 10;
        ctx.lineTo(cx, y + (Math.random() - 0.5) * 1.5);
      }
      ctx.stroke();
    }

    // Occasional darker knot/band
    for (let k = 0; k < 3; k++) {
      const kx = 60 + Math.random() * 400;
      const ky = 20 + Math.random() * 88;
      const kGrad = ctx.createRadialGradient(kx, ky, 2, kx, ky, 25 + Math.random() * 20);
      kGrad.addColorStop(0, 'rgba(80,40,10,0.25)');
      kGrad.addColorStop(1, 'rgba(80,40,10,0)');
      ctx.fillStyle = kGrad;
      ctx.fillRect(0, 0, 512, 128);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.repeat.set(3, 1); // 3 repeats along blade length
    return texture;
  }

  setAllColor(color) {
    this.setColor('all', color);
    this.setColor('beam', color);
  }

  getSpecInfo() {
    const widthInches = this.config.width / IN;
    const zones = this.zoneManager.getZones();
    const isMultiZone = zones.length > 1;
    const totalLengthInches = isMultiZone ? this.zoneManager.getTotalLength() : this.config.length / IN;
    const lengthInches = this.config.length / IN;

    return {
      widthFt: (widthInches / 12).toFixed(1),
      lengthFt: (totalLengthInches / 12).toFixed(1),
      widthInches,
      lengthInches: totalLengthInches,
      heightFt: (this.config.postHeight / IN / 12).toFixed(1),
      fasciaIn: (SPEC.FASCIA_HEIGHT / IN).toFixed(1),
      postIn: (SPEC.POST_SIZE / IN).toFixed(1),
      bladeChordIn: (SPEC.BLADE_CHORD / IN).toFixed(1),
      walls: { ...this.config.walls },
      structureType: this.wallManager.getStructureType(),
      postCount: this.wallManager.getPostCount(),
      cornerPostCount: this.wallManager.getCornerPostCount(),
      junctionPostCount: this.wallManager.getJunctionPostCount(),
      bayCount: this.wallManager.getBayCount(widthInches, totalLengthInches),
      postWarnings: this.wallManager.getWarnings(),
      userPostCount: this.wallManager.getUserPostCount(),
      removablePosts: this.wallManager.getRemovablePosts(),
      cantilever: { ...this.wallManager.cantilever },
      beams: this.beamManager.getBeams(),
      beamCounts: this.beamManager.getBeamCounts(),
      lighting: this.lightingManager.getEffectiveCounts(),
      lightingConfig: this.lightingManager.getConfig(),
      lightingLineItems: this.lightingManager.getLightingLineItems(widthInches, totalLengthInches),
      corbelEnds: this.config.corbelEnds,
      corniceStyle: this.config.corniceStyle,
      corniceLed: this.config.corniceLed,
      // Zone data
      zones: this.zoneManager.getSummary(),
      isMultiZone,
      scupperCount: this.zoneManager.getScupperCount(),
      beamToBeamCovers: this.zoneManager.getBeamToBeamCoverCount(),
    };
  }
}
